// The board (2026-10-26, graphics pass checkpoint 1; GRAPHICS-PASS.md C): a felt-and-sand tabletop built in code from
// the sim's map, no art files. One mesh for the board (felt grass, sand roads with soft worn edges, the forest floor
// darker, shade under every tree, the river's channel sunk into it) and its felt sides dropping to a wooden table;
// trees, one instanced draw a shape, that sway; one static mesh of tufts, flowers and rocks; the river
// (river.ts) as one painted water strip; the four stone bridges. Everything is read from sim.road and sim.blocked once
// at load: the map is the same every match.
// 2026-10-26, more realistic (the look of a photo of a real diorama): the felt's fibers and the sand's grain and
// pebbles from one detail tile made at load (detail.ts) with real relief; real trees (foliage.ts: trunks, limbs and
// leaf clusters in four kinds); grass blades and static-grass tufts along the roads; arched stone bridges with
// parapets and cobbled decks; water that mirrors the sky. Every color from palette.ts.
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { SIZE, type Sim } from '../sim/sim';
import { BASE } from '../sim/defs';
import { buildLanterns } from './buildings';
import { NOISE_GLSL, shared, stoneMaterial, weathered } from './look';
import { PALETTE } from './palette';
import { DETAIL, detailTile, hash3 as hash } from './detail';
import { grassMaterial, grassReach, grassPatch, growTree, treeDepthMaterial, treeMaterial, type TreeKind } from './foliage';
import { GRID, RIVER, depthAt, deckY, onBridge, riverHalf, riverMid, type Bridge } from './river';

/** How far the board's felt sides drop to the table. */
const DROP = 1.6;
/** Mask texels per meter. */
const MR = 2, MW = SIZE * MR;

/** Builds the world into the scene; returns what only Best and Ultra show (the full trees and the grass blades) and
 *  what only Light shows (the lite trees), for Stage.addTiered, and the lanterns' light pools (main.ts shows them
 *  while the lights are on). Phones get no blades (withGrass false) until one is measured: they run Best, and the
 *  blades are 650k triangles (Astra F1). */
export function buildWorld(scene: THREE.Scene, sim: Sim, bridges: Bridge[], withGrass = true) {
  const near = roadDistance(sim);
  const trees = placeTrees(sim, near), bushes = placeBushes(sim, near, trees), grass = withGrass ? grassMeshes(sim, near) : [];
  const t = treeMeshes([...trees, ...bushes]), lamps = buildLanterns(lanternSpots(sim, bridges));
  scene.add(board(sim, near, [...trees, ...bushes]), table(), ...t.best, ...t.light, scatter(sim, near), water(), ...bridgeMeshes(bridges), ...grass, lamps.posts, lamps.pools);
  return { best: [...t.best, ...grass], light: t.light, pools: lamps.pools };
}

/** Lanterns stand a step off a road's sand onto the verge, about LANTERN_GAP apart, those by a bridge's end placed first;
 *  none in the river's channel, on a bridge, or crowding a barracks or the center tower. ry turns the lamp to face its
 *  road. */
export const LANTERN_GAP = 26;
export function lanternSpots(sim: Sim, bridges: Bridge[]) {
  const isRoad = (i: number, j: number) => i >= 0 && j >= 0 && i < SIZE && j < SIZE && !!sim.road[j * SIZE + i];
  const cands: { x: number; z: number; ry: number; pri: number }[] = [];
  for (let j = 0; j < SIZE; j++) for (let i = 0; i < SIZE; i++) {
    if (!isRoad(i, j)) continue;
    for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      if (isRoad(i + di, j + dj) || isRoad(i + 2 * di, j + 2 * dj)) continue; // the verge, not a gap between two roads
      const x = i + 0.5 + di * 1.1, z = j + 0.5 + dj * 1.1;
      if (!offRiver(x, z, 0.8) || onBridge(bridges, x, z)) continue;
      if (sim.bases.some((b) => Math.hypot(b.x - x, b.y - z) < BASE.radius + 7) || Math.hypot(sim.center.x - x, sim.center.y - z) < 11) continue;
      // Anchor lamps to the actual ramp ends before filling the roads. Random bridge-adjacent
      // picks shifted the spacing grid when the river moved, leaving long unlit stretches.
      const endDistance = Math.min(...bridges.flatMap(b => [-1, 1].map(sign =>
        Math.hypot(x - b.x - sign * b.ux * b.hl, z - b.z - sign * b.uz * b.hl) / (b.hw + 2))));
      cands.push({ x, z, ry: Math.atan2(-dj, -di), pri: endDistance < 1
        ? endDistance : 1 + hash(i * 4 + di + 1, j * 4 + dj + 1, 90) });
    }
  }
  cands.sort((a, b) => a.pri - b.pri);
  const out: { x: number; z: number; ry: number }[] = [];
  for (const c of cands) if (out.every((o) => Math.hypot(o.x - c.x, o.z - c.z) >= LANTERN_GAP)) out.push({ x: c.x, z: c.z, ry: c.ry });
  return out;
}

/** Distance from each cell to the nearest walkable one, in cells (0 on a road); a two-pass chamfer. */
export function roadDistance(sim: Sim) {
  const d = new Float32Array(SIZE * SIZE);
  for (let k = 0; k < d.length; k++) d[k] = sim.blocked[k] && !sim.road[k] ? 1e9 : 0;
  const at = (i: number, j: number) => (i < 0 || j < 0 || i >= SIZE || j >= SIZE ? 1e9 : d[j * SIZE + i]);
  for (let j = 0; j < SIZE; j++) for (let i = 0; i < SIZE; i++) {
    const k = j * SIZE + i;
    d[k] = Math.min(d[k], at(i - 1, j) + 1, at(i, j - 1) + 1, at(i - 1, j - 1) + 1.414, at(i + 1, j - 1) + 1.414);
  }
  for (let j = SIZE - 1; j >= 0; j--) for (let i = SIZE - 1; i >= 0; i--) {
    const k = j * SIZE + i;
    d[k] = Math.min(d[k], at(i + 1, j) + 1, at(i, j + 1) + 1, at(i + 1, j + 1) + 1.414, at(i - 1, j + 1) + 1.414);
  }
  return d;
}

/** The kinds of tree (foliage.ts grows them): a spreading oak, a slim white birch, a pine, a low bush. */
export const KINDS = ['oak', 'birch', 'pine', 'bush'] as const;
export type Kind = TreeKind;
export type Tree = { x: number; z: number; r: number; tint: number; kind: Kind };

/** Which kind grows at a spot: pines in groves (a slow noise over the map), birches scattered among the oaks. */
function kindAt(x: number, z: number, h: number): Kind {
  const grove = (Math.sin(x * 0.11 + 1.3) + Math.sin(z * 0.09 - 0.7) + Math.sin((x + z) * 0.05)) / 3;
  return grove > 0.38 ? (h < 0.8 ? 'pine' : 'oak') : h < 0.2 ? 'birch' : h < 0.27 ? 'pine' : 'oak';
}

/** Dart throwing in a shuffled cell order over forest cells: a crown goes in where it doesn't crowd one already
 *  placed. `fits` says whether a cell can take a crown of radius r at x, z. */
function throwDarts(sim: Sim, salt: number, size: (i: number, j: number) => number, jitter: number, fits: (k: number, x: number, z: number, r: number) => boolean, placed: Tree[] = []) {
  const out: Tree[] = [], at: number[][] = Array.from({ length: SIZE * SIZE }, () => []);
  const all = [...placed];
  for (const [n, t] of placed.entries()) at[Math.floor(t.z) * SIZE + Math.floor(t.x)]?.push(n);
  const cells: number[] = [];
  for (let k = 0; k < SIZE * SIZE; k++) if (sim.blocked[k] && !sim.road[k]) cells.push(k);
  const order = cells.map((k) => [hash(k % SIZE, (k / SIZE) | 0, 7 + salt), k]).sort((a, b) => a[0] - b[0]);
  for (const [, k] of order) {
    const i = k % SIZE, j = (k / SIZE) | 0;
    const x = i + 0.5 + (hash(i, j, 1 + salt) - 0.5) * jitter, z = j + 0.5 + (hash(i, j, 2 + salt) - 0.5) * jitter;
    const r = size(i, j);
    if (!fits(k, x, z, r)) continue;
    let crowded = false;
    for (let b = Math.max(0, j - 3); b <= Math.min(SIZE - 1, j + 3) && !crowded; b++) for (let a = Math.max(0, i - 3); a <= Math.min(SIZE - 1, i + 3) && !crowded; a++)
      for (const t of at[b * SIZE + a]) if (Math.hypot(all[t].x - x, all[t].z - z) < (all[t].r + r) * 0.7) { crowded = true; break; }
    if (crowded) continue;
    at[k].push(all.length);
    const tint = hash(i, j, 4 + salt), tree: Tree = { x, z, r, tint, kind: 'bush' };
    all.push(tree); out.push(tree);
  }
  return out;
}
/** Nothing within r of x, z sinks into the river's channel: the middle and eight points round the rim (four missed a
 *  bank cutting across a corner, Astra F2). */
const offRiver = (x: number, z: number, r: number) => {
  if (depthAt(x, z) !== 0) return false;
  for (let k = 0; k < 8; k++) if (depthAt(x + Math.cos(k * 0.785) * r, z + Math.sin(k * 0.785) * r) !== 0) return false;
  return true;
};

/** Trees over all the forest, evenly, so blocked ground always reads as forest. Each is kept back from the roads by
 *  its own crown (no soldier walks under one) and off the river's channel and banks. */
export function placeTrees(sim: Sim, near: Float32Array): Tree[] {
  // near is cell to cell; the tree sits up to 0.64 off its cell's middle and a road cell reaches 0.71 past its own
  // sizes from a sapling to a big old tree (most in between); small clearings deep in the forest, never at its edge
  const out = throwDarts(sim, 0, (i, j) => 0.85 + Math.pow(hash(i, j, 3), 1.4) * 0.8, 0.9, (k, x, z, r) => near[k] >= r * 0.6 + 1.4 && offRiver(x, z, r) && !(near[k] > 6 && clearing(x, z)));
  for (const t of out) t.kind = kindAt(t.x, t.z, t.tint);
  return out;
}

/** A clearing in the deep forest: a slow noise over the map, high in a few soft patches. */
const clearing = (x: number, z: number) => Math.sin(x * 0.23 + 2) + Math.sin(z * 0.19 - 1) + Math.sin((x - z) * 0.13) > 1.75;

/** Bushes in the gaps the trees leave: thick along the forest's edge, nearer the road than a tree may stand (smaller
 *  crowns, less jitter: up to 0.3 off the cell's middle), never over it; now and then under the trees, as undergrowth. */
export function placeBushes(sim: Sim, near: Float32Array, trees: Tree[]): Tree[] {
  return throwDarts(sim, 40, (i, j) => 0.45 + hash(i, j, 43) * 0.35, 0.6,
    (k, x, z, r) => near[k] >= r * 0.6 + 1.05 && hash(k, 3, 44) < (near[k] < 4 ? 0.55 : 0.2) && offRiver(x, z, r), trees);
}

/** The map as a texture the board's shader reads: road (softened), forest depth (how far from a road), and shade
 *  under trees and buildings. */
function maskTexture(sim: Sim, near: Float32Array, trees: Tree[]) {
  const road = new Float32Array(MW * MW), ao = new Float32Array(MW * MW);
  for (let j = 0; j < MW; j++) for (let i = 0; i < MW; i++) road[j * MW + i] = sim.road[((j / MR) | 0) * SIZE + ((i / MR) | 0)] ? 1 : 0;
  blur(road, 3); blur(road, 3); blur(road, 2);
  const splat = (x: number, z: number, r: number, s: number) => {
    for (let j = Math.max(0, Math.floor((z - r) * MR)); j < Math.min(MW, (z + r) * MR); j++)
      for (let i = Math.max(0, Math.floor((x - r) * MR)); i < Math.min(MW, (x + r) * MR); i++) {
        const d = Math.hypot((i + 0.5) / MR - x, (j + 0.5) / MR - z) / r;
        if (d < 1) ao[j * MW + i] += s * (1 - d * d);
      }
  };
  for (const t of trees) splat(t.x + 0.3, t.z - 0.2, t.r * 1.25, t.kind === 'bush' ? 0.4 : 0.55);
  for (const b of sim.bases) splat(b.x, b.y, BASE.radius + 3, 0.5);
  splat(sim.center.x, sim.center.y, 7, 0.5);
  blur(ao, 1);
  const data = new Uint8Array(MW * MW * 4);
  for (let j = 0; j < MW; j++) for (let i = 0; i < MW; i++) {
    const k = j * MW + i;
    data[k * 4] = Math.round(road[k] * 255);
    data[k * 4 + 1] = Math.round(Math.min(1, near[((j / MR) | 0) * SIZE + ((i / MR) | 0)] / 8) * 255);
    data[k * 4 + 3] = Math.round(Math.min(1, ao[k]) * 255);
  }
  const tex = new THREE.DataTexture(data, MW, MW, THREE.RGBAFormat);
  tex.magFilter = tex.minFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/** The detail tile (detail.ts) as a repeating, mipmapped texture: the mips are what keep fibers and grain from
 *  sparkling when the camera pulls back. */
let detailTex: THREE.DataTexture | null = null;
function detailTexture() {
  if (detailTex) return detailTex;
  const t = new THREE.DataTexture(detailTile(), DETAIL, DETAIL, THREE.RGBAFormat);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearMipmapLinearFilter; t.generateMipmaps = true; t.anisotropy = 4;
  t.needsUpdate = true;
  return (detailTex = t);
}

/** A separable box blur of radius r texels, in place. */
function blur(a: Float32Array, r: number) {
  const tmp = new Float32Array(a.length);
  for (const [sx, sy] of [[1, MW], [MW, 1]]) {
    for (let j = 0; j < MW; j++) for (let i = 0; i < MW; i++) {
      let s = 0, n = 0;
      for (let k = -r; k <= r; k++) { const ii = i + k; if (ii < 0 || ii >= MW) continue; s += a[j * sy + ii * sx]; n++; }
      tmp[j * sy + i * sx] = s / n;
    }
    a.set(tmp);
  }
}

/** A uniform holding one of the palette's Colors itself (palette.ts), so a change there shows. */
const c = (color: THREE.Color) => ({ value: color });
/** Meters a detail tile covers on the felt and on the sand (sampled at both, offset, so the two never line up). */
const FELT_TILE = 4, SAND_TILE = 7;
/** The ground's colors and the detail tile, one set shared by the board and the bridge decks (whose sand has to run on
 *  from the road's without a seam). */
let ground: Record<string, { value: unknown }> | null = null;
const G = PALETTE.ground;
const groundUniforms = () => (ground ??= {
  uDetail: { value: detailTexture() },
  uGrassA: c(G.grassA), uGrassB: c(G.grassB), uForest: c(G.forest), uSandA: c(G.sandA), uSandB: c(G.sandB),
  uEdge: c(G.edge), uBank: c(G.bank), uSide: c(G.side), uPebble: c(G.pebble), uSheen: c(G.sheen),
});
/** Sand at world x, z: grain, color drift, pale pebbles standing out of it; h is its relief in meters. */
const SAND_GLSL = `
uniform sampler2D uDetail; uniform vec3 uSandA, uSandB, uPebble;
vec3 bdSand(vec2 w, out float h, out vec4 dS, out float n2) {
  dS = texture2D(uDetail, w / ${SAND_TILE.toFixed(1)} + 0.37);
  n2 = texture2D(uDetail, w / 7.3 + 0.61).a;
  vec3 sand = mix(uSandA, uSandB, n2) * (0.92 + 0.16 * dS.g);
  float pebble = smoothstep(0.62, 0.7, dS.g);
  h = 0.01 * dS.g + 0.012 * pebble; // low relief: a steeper pebble threw a dark speck on its shaded side
  return mix(sand, uPebble * (0.7 + 0.6 * dS.b), pebble * 0.7);
}`;

function board(sim: Sim, near: Float32Array, trees: Tree[]) {
  // the top, a vertex every half meter, sunk where the river runs
  const top = new THREE.PlaneGeometry(SIZE, SIZE, SIZE * GRID, SIZE * GRID).rotateX(-Math.PI / 2).translate(SIZE / 2, 0, SIZE / 2);
  const pos = top.attributes.position;
  for (let k = 0; k < pos.count; k++) pos.setY(k, -depthAt(pos.getX(k), pos.getZ(k)));
  top.computeVertexNormals();
  // the felt sides, down to the table
  const side = (x: number, z: number, ry: number) => new THREE.PlaneGeometry(SIZE, DROP).rotateY(ry).translate(x, -DROP / 2, z);
  const geo = mergeGeometries([top, side(SIZE / 2, SIZE, 0), side(SIZE / 2, 0, Math.PI), side(SIZE, SIZE / 2, Math.PI / 2), side(0, SIZE / 2, -Math.PI / 2)])!;
  const mat = new THREE.MeshLambertMaterial();
  const u = { ...groundUniforms(), uMask: { value: maskTexture(sim, near, trees) }, uFine: shared.uFine, uWetB: shared.uWet, uRain: shared.uRain, uTime: shared.uTime };
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vW; varying vec3 vWN;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vW = (modelMatrix * vec4(transformed, 1.0)).xyz; vWN = objectNormal;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
varying vec3 vW; varying vec3 vWN;
uniform sampler2D uMask; uniform vec3 uGrassA, uGrassB, uForest, uEdge, uBank, uSide, uSheen; uniform float uFine, uWetB, uRain, uTime;` + NOISE_GLSL + SAND_GLSL)
      .replace('#include <color_fragment>', `#include <color_fragment>
  float lkH, lkFelt, lkPud = 0.0, lkSnowMul = 1.0;
  vec2 lkWxy = vW.xz;
#define LK_SNOW_MUL lkSnowMul
  {
    vec2 w = vW.xz;
    vec4 m = texture2D(uMask, w / ${SIZE.toFixed(1)});
    vec4 dF = texture2D(uDetail, w / ${FELT_TILE.toFixed(1)}), dS;
    float sandH, n2;
    vec3 sand = bdSand(w, sandH, dS, n2);
    // broad and middle mottling from the tile's slow channel, read at scales that don't share a period
    float n1 = texture2D(uDetail, w / 41.0 + 0.13).a, n3 = texture2D(uDetail, w / 17.0 + 0.29).a;
    vec3 grass = mix(uGrassA, uGrassB, smoothstep(0.3, 0.75, n1));
    grass *= (0.86 + 0.2 * n2) * (0.8 + 0.4 * dF.r); // felt: soft mottling, and the nap's fibers catching more or less light
    grass = mix(grass, uForest, smoothstep(0.08, 0.6, m.g) * 0.75);
    float rt = m.r + (n3 - 0.5) * 0.4 + (n2 - 0.5) * 0.14;
    float road = smoothstep(0.42, 0.52, rt);
    float edge = smoothstep(0.26, 0.42, rt) * (1.0 - road);
    sand *= 0.9 + 0.1 * smoothstep(0.42, 0.62, rt); // the sand's rim a shade darker, where wheels and feet wore it in
    vec3 col = mix(grass, sand, road);
    col = mix(col, uEdge, edge * 0.45);
    col *= 1.0 - 0.5 * m.a;
    float bank = 1.0 - smoothstep(-0.45, -0.04, vW.y);
    col = mix(col, uBank * (0.85 + 0.3 * dS.g), bank);
    bool isSide = vWN.y < 0.5;
    if (isSide) col = uSide * (0.82 + 0.1 * smoothstep(-${DROP.toFixed(1)}, 0.0, vW.y)) * (0.86 + 0.28 * dF.r);
    diffuseColor.rgb = col;
    // relief in meters: felt fibers a centimeter or two, sand grain, pebbles standing out; less where a pixel is large
    float far = clamp(1.0 - length(fwidth(w)) * 3.0, 0.25, 1.0);
    lkFelt = isSide ? 0.0 : (1.0 - road) * (1.0 - bank);
    lkH = far * mix(0.02 * dF.r, sandH, road);
    // weather (look.ts weathered does the snow and the wet): feet and wheels keep the roads' snow thin, and in the rain
    // the road's low spots hold puddles that darken and catch the sky, rain rings on them
    lkSnowMul = isSide ? 0.0 : 1.0 - 0.7 * road;
    lkPud = isSide ? 0.0 : uWetB * road * smoothstep(0.64, 0.7, texture2D(uDetail, w / 9.0 + 0.71).a);
    diffuseColor.rgb *= 1.0 - 0.5 * lkPud;
  }`)
      .replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n  if (uFine > 0.5) normal = lkBump(-vViewPosition, normal, lkH); // Light skips the relief')
      .replace('#include <opaque_fragment>', `if (uFine > 0.5) {
    // felt's sheen: the fibers light up toward the edge of the view (velvet), only on the grass
    float rim = 1.0 - clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0);
    outgoingLight += uSheen * rim * rim * lkFelt;
  }
  if (lkPud > 0.003) outgoingLight += uSkyCol * lkPud * (0.06 + 0.5 * lkRipple(lkWxy, uTime, uRain));
  #include <opaque_fragment>`);
    weathered(sh);
  };
  mat.customProgramCacheKey = () => 'board';
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  return mesh;
}

/** The wooden table the board stands on: planks with grain lines and knots, and shade where the board meets it. */
function table() {
  const mat = new THREE.MeshLambertMaterial();
  const u = { uWoodA: c(PALETTE.wood.a), uWoodB: c(PALETTE.wood.b) };
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vW; uniform vec3 uWoodA, uWoodB;' + NOISE_GLSL)
      .replace('#include <color_fragment>', `#include <color_fragment>
  {
    float plank = floor(vW.z / 3.2), f = fract(vW.z / 3.2);
    float grain = lkFbm(vec2(vW.x * 0.08 + plank * 13.0, vW.z * 2.2));
    vec3 col = mix(uWoodA, uWoodB, grain * 0.8 + 0.25 * lkHash(vec2(plank, 3.0)));
    // the grain's lines: the growth rings cut lengthwise, bent around by the slow grain
    float lines = abs(fract(vW.z * 2.6 + plank * 0.37 + grain * 3.0) - 0.5);
    col *= 0.9 + 0.1 * smoothstep(0.0, 0.18, lines);
    col *= mix(0.55, 1.0, smoothstep(0.0, 0.03, f) * (1.0 - smoothstep(0.97, 1.0, f)));
    vec2 o = max(abs(vW.xz - ${(SIZE / 2).toFixed(1)}) - ${(SIZE / 2).toFixed(1)}, 0.0);
    col *= 1.0 - 0.55 * exp(-length(o) * 0.45);
    diffuseColor.rgb = col;
  }`);
  };
  mat.customProgramCacheKey = () => 'table';
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2400, 2400).rotateX(-Math.PI / 2).translate(SIZE / 2, -DROP, SIZE / 2), mat);
  mesh.receiveShadow = true;
  return mesh;
}

/** How many shapes each kind is grown in (foliage.ts), one draw each: the broadleaves two, so neighbours differ; pines
 *  and bushes one, their turn, size and tint vary them enough. Every draw counts on Light (it must stay as fast as
 *  main; a draw is most of its frame). */
export const SHAPES: Record<Kind, number> = { oak: 2, birch: 2, pine: 1, bush: 1 };

/** The trees: on Best and Ultra each kind in its shapes (SHAPES), full detail; on Light one lite shape a kind (no twigs,
 *  half the leaf cards, foliage.ts), so Light draws fewer triangles and calls than main did. */
function treeMeshes(trees: Tree[]) {
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0), col = new THREE.Color(), p = new THREE.Vector3(), sc = new THREE.Vector3();
  const best: THREE.InstancedMesh[] = [], light: THREE.InstancedMesh[] = [];
  const mesh = (geo: THREE.BufferGeometry, mat: THREE.Material, mine: Tree[], kind: Kind) => {
    const out = new THREE.InstancedMesh(geo, mat, Math.max(1, mine.length));
    // each tree's own draw: whether it wears its kind's accent this season (foliage.ts TREE_SEASON)
    const draw = new Float32Array(Math.max(1, mine.length));
    geo.setAttribute('aTree', new THREE.InstancedBufferAttribute(draw, 1));
    mine.forEach((t, k) => {
      const id = Math.floor(t.x * 977) ^ Math.floor(t.z * 613); // the same tree looks the same on every tier
      draw[k] = hash(id, 9, 6);
      q.setFromAxisAngle(up, t.tint * 6.28);
      out.setMatrixAt(k, m.compose(p.set(t.x, 0, t.z), q, sc.set(t.r, t.r * (0.88 + hash(id, 3, 6) * 0.3), t.r)));
      // the hue is the palette's; a tree only shifts it a little lighter or darker, warmer or cooler
      const l = 0.86 + hash(id, 5, 6) * 0.26, w = (hash(id, 7, 6) - 0.5) * 0.12;
      out.setColorAt(k, col.setRGB(l * (1 + w), l, l * (1 - w)));
    });
    out.count = mine.length;
    out.customDepthMaterial = treeDepthMaterial(kind);
    out.castShadow = kind !== 'bush'; out.receiveShadow = true;
    return out;
  };
  for (const kind of KINDS) {
    const [leafA, leafB] = PALETTE.foliage[kind], mat = treeMaterial(kind, leafA, leafB, PALETTE.bark[kind === 'bush' ? 'oak' : kind]);
    const all = trees.filter((t) => t.kind === kind);
    for (let v = 0; v < SHAPES[kind]; v++) best.push(mesh(growTree(kind, v), mat, all.filter((t) => Math.floor(t.tint * 97) % SHAPES[kind] === v), kind));
    light.push(mesh(growTree(kind, 0, true), mat, all, kind));
  }
  return { best, light };
}

/** Grass blades on the felt: patches thick along the road's verge and the forest's edge, thinning into the forest, none
 *  in the river. Grouped in big squares so the camera skips the ones it can't see. Best and Ultra only (main.ts hands
 *  them to the Stage); Light keeps the tufts. */
export const GRASS_CHUNK = 48;
export function placeGrass(sim: Sim, near: Float32Array) {
  const out: { x: number; z: number; s: number; ry: number }[] = [], reach = grassReach();
  for (let j = 0; j < SIZE; j++) for (let i = 0; i < SIZE; i++) {
    const k = j * SIZE + i;
    if (!sim.blocked[k] || sim.road[k]) continue;
    const d = near[k], n = d <= 1.5 ? 3 : d <= 3 ? 2 : d <= 6 ? 1 : hash(i, j, 70) < 0.3 ? 1 : 0;
    for (let t = 0; t < n; t++) {
      const x = i + hash(i, j, 71 + t), z = j + hash(i, j, 74 + t), sc = 0.75 + 0.6 * hash(i, j, 77 + t) * (d <= 3 ? 1 : 0.7);
      // the whole patch on flat ground, bent blades and breeze included, none of it over the river's sunk bank (Astra
      // F2 and its re-check); a verge's blades may lean a hand's width onto the road's sand, as the tufts there do
      if (!offRiver(x, z, reach * sc + 0.1)) continue;
      out.push({ x, z, s: sc, ry: hash(i, j, 80 + t) * 6.283 });
    }
  }
  return out;
}

export function grassMeshes(sim: Sim, near: Float32Array) {
  const all = placeGrass(sim, near), per = SIZE / GRASS_CHUNK, geo = grassPatch(), G = PALETTE.grass;
  const mat = grassMaterial(G.root, G.tip, G.dry), m = new THREE.Matrix4(), q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0), p = new THREE.Vector3(), sc = new THREE.Vector3();
  const out: THREE.InstancedMesh[] = [];
  for (let cj = 0; cj < per; cj++) for (let ci = 0; ci < per; ci++) {
    const mine = all.filter((g) => Math.floor(g.x / GRASS_CHUNK) === ci && Math.floor(g.z / GRASS_CHUNK) === cj);
    if (!mine.length) continue;
    const mesh = new THREE.InstancedMesh(geo, mat, mine.length);
    mine.forEach((g, k) => mesh.setMatrixAt(k, m.compose(p.set(g.x, 0, g.z), q.setFromAxisAngle(up, g.ry), sc.set(g.s, g.s, g.s))));
    mesh.computeBoundingSphere();
    mesh.receiveShadow = true;
    out.push(mesh);
  }
  return out;
}

/** The scatter's colors, by slot: each vertex names one (aSlot) and the shader reads it from PALETTE by reference, so
 *  a season's paint reaches the verge too (2026-10-26: baked vertex colors kept the first match's season). */
export const SLOT = { root: 0, tip: 1, dry: 2, mound: 3, rock: 4, bankRock: 5, flower: 6 } as const;
export const scatterSlots = () => { const G = PALETTE.grass, S = PALETTE.scatter; return [G.root, G.tip, G.dry, S.mound, S.rock, S.bankRock, ...S.flowers]; };

/** Static-grass tufts, flower tufts and rocks, merged into one static mesh: tufts on the grass beside the roads and
 *  now and then on a road's edge (a real diorama's paths have them), rocks along the forest's edge and the river's
 *  banks. */
function scatter(sim: Sim, near: Float32Array) {
  const parts: THREE.BufferGeometry[] = [];
  // aFlower: a flower's own draw (0 for the rest), against the season's share out (shared.uFlowers)
  const add = (g: THREE.BufferGeometry, slot: number, x: number, y: number, z: number, s: number, ry = 0, flower = 0) => {
    const n = g.index ? g.toNonIndexed() : g.clone();
    n.scale(s, s, s).rotateY(ry).translate(x, y, z);
    n.setAttribute('aFlower', new THREE.Float32BufferAttribute(new Float32Array(n.attributes.position.count).fill(flower), 1));
    if (!n.attributes.aSlot) n.setAttribute('aSlot', new THREE.Float32BufferAttribute(new Float32Array(n.attributes.position.count).fill(slot), 1));
    if (n.attributes.uv) n.deleteAttribute('uv');
    parts.push(n);
  };
  const petal = new THREE.IcosahedronGeometry(0.09, 0), leaf = new THREE.SphereGeometry(0.22, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2);
  const rock = new THREE.DodecahedronGeometry(0.5, 0), tufts = [0, 1, 2].map(tuftGeometry);
  const FLOWERS = [0, 1, 2, 3, 0].map((f) => SLOT.flower + f); // white twice as often
  const isRoad = (i: number, j: number) => i >= 0 && j >= 0 && i < SIZE && j < SIZE && !!sim.road[j * SIZE + i];
  for (let j = 0; j < SIZE; j++) for (let i = 0; i < SIZE; i++) {
    const k = j * SIZE + i, h = hash(i, j, 9), x = i + 0.5 + (hash(i, j, 10) - 0.5) * 0.7, z = j + 0.5 + (hash(i, j, 11) - 0.5) * 0.7;
    if (sim.road[k]) { // a road cell at the road's edge: a tuft now and then where feet don't wear it away
      const edge = !isRoad(i - 1, j) || !isRoad(i + 1, j) || !isRoad(i, j - 1) || !isRoad(i, j + 1);
      if (edge && h < 0.22 && depthAt(x, z) === 0) add(tufts[(h * 30) % 3 | 0], 0, x, 0, z, 0.8 + hash(i, j, 15) * 0.5, h * 70);
      continue;
    }
    if (!sim.blocked[k]) continue;
    if (depthAt(x, z) > 0) { // the channel: rocks on the bank's top, now and then
      if (h < 0.05 && depthAt(x, z) < 0.25) add(rock, SLOT.bankRock, x, -0.1, z, 0.55 + hash(i, j, 12) * 0.5, h * 60);
      continue;
    }
    if (near[k] > 3.5) continue;
    if (h < 0.11) { // a flower tuft: a low green mound with a few flowers in one color
      const fc = FLOWERS[(hash(i, j, 13) * FLOWERS.length) | 0];
      add(leaf, SLOT.mound, x, 0, z, 1);
      const draw = 0.01 + 0.98 * hash(i, j, 16);
      for (let p = 0; p < 4; p++) { const a = p * 1.7 + h * 40; add(petal, fc, x + Math.cos(a) * 0.15, 0.17 + (p % 2) * 0.05, z + Math.sin(a) * 0.15, 1, 0, draw); }
    } else if (h < 0.14) add(rock, SLOT.rock, x, 0.05, z, 0.4 + hash(i, j, 14) * 0.5, h * 90);
    // grass tufts on the verge, thicker near the road
    for (let t = 0; t < 3; t++) {
      if (hash(i, j, 20 + t) > 0.75 - near[k] * 0.15) continue;
      add(tufts[t], 0, i + hash(i, j, 23 + t), 0, j + hash(i, j, 26 + t), 0.7 + hash(i, j, 29 + t) * 0.7, hash(i, j, 32 + t) * 6.28);
    }
  }
  const geo = mergeGeometries(parts.map((g) => { for (const n of Object.keys(g.attributes)) if (!['position', 'normal', 'aSlot', 'aFlower'].includes(n)) g.deleteAttribute(n); return g; }))!;
  const mat = new THREE.MeshLambertMaterial({ vertexColors: true }), slots = scatterSlots(); // vertexColors: for vColor
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uFlowers = shared.uFlowers;
    sh.uniforms.uSlots = { value: slots };
    // a flower past the season's share folds away to a point (no pixels)
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>\nattribute float aFlower; attribute float aSlot; uniform float uFlowers; uniform vec3 uSlots[${slots.length}];`)
      .replace('#include <color_vertex>', 'vColor = uSlots[int(aSlot + 0.5)];')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  if (aFlower > uFlowers) transformed = vec3(0.0);');
    weathered(sh);
  };
  mat.customProgramCacheKey = () => 'scatter';
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  return mesh;
}

/** A static-grass tuft: a few thin blades fanning up and out from one spot, olive at the root and sun-yellow at the
 *  tips (scatter color slots), lit as the ground (their normals point up) so they sit in the felt instead of glinting. */
export function tuftGeometry(v: number) {
  const pos: number[] = [], col: number[] = [], nrm: number[] = [];
  const root = SLOT.root, tip = v === 2 ? SLOT.dry : SLOT.tip;
  const blades = 9;
  for (let b = 0; b < blades; b++) {
    const a = (b / blades) * Math.PI * 2 + v, lean = 0.06 + hash(b, v, 60) * 0.1, hgt = 0.16 + hash(b, v, 61) * 0.14, w = 0.045;
    const cx = Math.cos(a), cz = Math.sin(a), px = -cz * w, pz = cx * w;
    // both windings, each with the up-leaning normal: a double-sided material would flip it on the back face and
    // draw the blade dark (the first shots showed black ticks all over the verges)
    pos.push(px, 0, pz, -px, 0, -pz, cx * lean, hgt, cz * lean, -px, 0, -pz, px, 0, pz, cx * lean, hgt, cz * lean);
    for (let k = 0; k < 2; k++) col.push(root, root, tip);
    for (let k = 0; k < 6; k++) nrm.push(cx * 0.3, 1, cz * 0.3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aSlot', new THREE.Float32BufferAttribute(col, 1));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm.map((x, k) => x / Math.hypot(nrm[k - (k % 3)], nrm[k - (k % 3) + 1], nrm[k - (k % 3) + 2])), 3));
  return g;
}

/** The river: one strip around the ring, wider than the water so its edges sit under the banks (the board hides them).
 *  uv.x runs along it (meters), uv.y is how far past the water's half width a point is (the foam line is near 0).
 *  Standard and smooth, so it mirrors the sky (scene.environment); the flow's streaks ripple its surface. */
function water() {
  const N = 480, rows = [-1, -0.001, 0, 0.001, 1];
  const pos: number[] = [], uv: number[] = [], idx: number[] = [];
  let s = 0, px = 0, pz = 0;
  for (let k = 0; k <= N; k++) {
    const a = (k / N) * Math.PI * 2, r = riverMid(a), hw = riverHalf(a), out = hw + RIVER.bank + 0.3;
    const cx = SIZE / 2 + Math.cos(a) * r, cz = SIZE / 2 + Math.sin(a) * r;
    if (k) s += Math.hypot(cx - px, cz - pz);
    px = cx; pz = cz;
    for (const t of rows) {
      const off = t === 0 ? 0 : Math.abs(t) < 0.5 ? Math.sign(t) * hw : Math.sign(t) * out;
      pos.push(SIZE / 2 + Math.cos(a) * (r + off), RIVER.water, SIZE / 2 + Math.sin(a) * (r + off));
      uv.push(s, Math.abs(off) - hw);
    }
  }
  for (let k = 0; k < N; k++) for (let t = 0; t < rows.length - 1; t++) {
    const a = k * rows.length + t, b = a + rows.length;
    idx.push(a, b, a + 1, a + 1, b, b + 1); // counterclockwise seen from above (radial x tangential points down)
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(pos.map((_, k) => (k % 3 === 1 ? 1 : 0)), 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(idx);
  const mat = new THREE.MeshStandardMaterial({ roughness: 0.1, metalness: 0 });
  const u = { uDeep: c(PALETTE.water.deep), uShallow: c(PALETTE.water.shallow), uFoam: c(PALETTE.water.foam), uSnowCol: c(PALETTE.snow), uIce: shared.uIce, uRain: shared.uRain, uCover: shared.uCover, uLamps: shared.uLamps };
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u, { uTime: shared.uTime });
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vRiver;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vRiver = uv;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vRiver; uniform vec3 uDeep, uShallow, uFoam, uSnowCol; uniform float uTime, uIce, uRain, uCover, uLamps;' + NOISE_GLSL)
      .replace('#include <color_fragment>', `#include <color_fragment>
  float lkH;
  {
    float edge = vRiver.y; // meters past the water's half width: about -2 in the middle, the bank meets it near 0.4
    vec3 col = mix(uDeep, uShallow, smoothstep(-1.6, 0.2, edge));
    // the painted blue is a daylight color: greyer under rain, deeper once the lamps are lit (2026-10-26 night stills:
    // the river glowed electric blue under the moon)
    col = mix(col, vec3(dot(col, vec3(0.3, 0.59, 0.11))), 0.35 * uRain) * (1.0 - 0.38 * uLamps - 0.15 * uRain);
    // painted flow: soft streaks drifting downstream, and a few glints
    float flow = lkNoise(vec2(vRiver.x * 0.35 - uTime * 0.6, edge * 1.6));
    col += vec3(0.12, 0.16, 0.18) * smoothstep(0.62, 0.9, flow);
    vec2 g = vec2(vRiver.x * 4.0 - uTime * 1.6, edge * 5.0), gf = fract(g) - 0.5;
    float glint = step(0.97, lkHash(floor(g))) * (1.0 - smoothstep(0.08, 0.3, length(gf * vec2(1.0, 1.6))));
    col += vec3(0.6) * glint * (1.0 - smoothstep(-1.2, -0.2, edge));
    // a foam line where the water meets the bank, breaking up as it goes
    float foam = smoothstep(-0.15, 0.25, edge + 0.15 * sin(vRiver.x * 2.3 + uTime * 1.5)) * (0.55 + 0.45 * lkNoise(vec2(vRiver.x * 1.5, uTime * 0.5)));
    col = mix(col, uFoam, foam * 0.8);
    // rain rings on the open water
    col += vec3(0.45) * lkRipple(vec2(vRiver.x, edge * 2.0), uTime, uRain);
    // winter: ice along both banks, pale and still with a few cracks (snow on it where the snow lies), the open water
    // between darker and slower
    float ice = uIce * smoothstep(-1.15, -0.7, edge + 0.35 * (lkNoise(vec2(vRiver.x * 0.5, 1.7)) - 0.5));
    float crack = 1.0 - smoothstep(0.0, 0.04, abs(lkNoise(vec2(vRiver.x * 1.1, edge * 2.6)) - 0.5));
    vec3 iceCol = mix(vec3(0.74, 0.84, 0.92), uSnowCol, uCover * smoothstep(-0.5, 0.2, edge)) * (1.0 - 0.18 * crack);
    diffuseColor.rgb = mix(col * (1.0 - 0.18 * uIce), iceCol, ice);
    // ripples: the gloss's surface, small waves drifting with the flow (none on the ice)
    lkH = 0.025 * (flow + 0.5 * lkNoise(vec2(vRiver.x * 1.3 - uTime * 0.9, edge * 3.1))) * (1.0 - ice);
  }`)
      .replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n  normal = lkBump(-vViewPosition, normal, lkH);');
  };
  mat.customProgramCacheKey = () => 'river';
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  return mesh;
}

/** Parapets and their coping, and the end posts, are this tall and thick. */
const WALL = { h: 1.05, t: 0.55, cap: 0.13, capW: 0.72 };

/** The bridges: each an arched stone bridge whose deck humps over the water (river.ts deckY, which the soldiers ride),
 *  the arch the water runs through ringed with voussoirs on both faces, a parapet with a coping along each edge and a
 *  post at each end. A deck paved edge to edge, as wide as these roads, read from above as a big grey square
 *  (2026-10-26 shots): the road's sand runs over its middle (deckMaterial), so it reads as the road crossing a bridge. Three meshes:
 *  the walls in courses (stoneMaterial), the arch rings in stones laid along their uv (setts), the deck (cobbles at the
 *  edges, sand down the middle). */
function bridgeMeshes(bridges: Bridge[]) {
  const walls: THREE.BufferGeometry[] = [], paved: THREE.BufferGeometry[] = [], decks: THREE.BufferGeometry[] = [];
  const lighter = new THREE.Color(1.08, 1.06, 1.02), plain = new THREE.Color(1, 1, 1);
  const tint = (g: THREE.BufferGeometry, cc: THREE.Color) => { const n = g.index ? g.toNonIndexed() : g; const a = new Float32Array(n.attributes.position.count * 3); for (let k = 0; k < a.length; k += 3) cc.toArray(a, k); n.setAttribute('color', new THREE.Float32BufferAttribute(a, 3)); if (n.attributes.uv) n.deleteAttribute('uv'); return n; };
  for (const b of bridges) {
    const hl = b.hl, hw = b.hw, arch = riverHalf(Math.atan2(b.z - SIZE / 2, b.x - SIZE / 2)) + 0.9, bottom = -RIVER.depth - 0.2, rise = deckY(0, hl) - 0.5 - bottom;
    const curve = (k: number, n: number, y0: number) => { const u = -hl + (k / n) * 2 * hl; return [u, deckY(u, hl) + y0] as const; };
    // the body: a side profile in (u, y) with the arch cut from its foot, extruded across the road (v)
    const body = new THREE.Shape();
    body.moveTo(-hl, bottom); body.lineTo(-arch, bottom);
    body.absellipse(0, bottom, arch, rise, Math.PI, 0, true);
    body.lineTo(hl, bottom);
    for (let k = 24; k >= 0; k--) body.lineTo(...curve(k, 24, -0.02));
    const out = hw + WALL.t; // the body reaches under the parapets
    const slab = new THREE.ExtrudeGeometry(body, { depth: out * 2, bevelEnabled: false, curveSegments: 20 }).translate(0, 0, -out);
    const parts: [THREE.BufferGeometry, THREE.Color][] = [[slab, plain]];
    for (const sgn of [-1, 1]) {
      // the parapet, then its coping a little wider, following the hump
      const wall = new THREE.Shape(), cap = new THREE.Shape();
      for (let k = 0; k <= 24; k++) (k ? wall.lineTo : wall.moveTo).call(wall, ...curve(k, 24, WALL.h));
      for (let k = 24; k >= 0; k--) wall.lineTo(...curve(k, 24, -0.05));
      for (let k = 0; k <= 24; k++) (k ? cap.lineTo : cap.moveTo).call(cap, ...curve(k, 24, WALL.h + WALL.cap));
      for (let k = 24; k >= 0; k--) cap.lineTo(...curve(k, 24, WALL.h));
      const z = sgn > 0 ? hw : -hw - WALL.t, zc = z - (WALL.capW - WALL.t) / 2;
      parts.push([new THREE.ExtrudeGeometry(wall, { depth: WALL.t, bevelEnabled: false }).translate(0, 0, z), plain]);
      parts.push([new THREE.ExtrudeGeometry(cap, { depth: WALL.capW, bevelEnabled: false }).translate(0, 0, zc), lighter]);
      for (const e of [-1, 1]) {
        const px = e * (hl - 0.45), pz = z + WALL.t / 2;
        parts.push([new THREE.BoxGeometry(0.9, WALL.h + 0.5, 0.9).translate(px, (WALL.h + 0.5) / 2 - 0.05, pz), plain]);
        parts.push([new THREE.ConeGeometry(0.72, 0.45, 4).rotateY(Math.PI / 4).translate(px, WALL.h + 0.45 + 0.22, pz), lighter]);
      }
      // the arch's ring of voussoirs, standing a hand's width proud of the face
      paved.push(archRing(arch, rise, bottom, 0.55, sgn > 0 ? out : -out, sgn));
    }
    // into the world: u along the road, v across it
    const m = new THREE.Matrix4().makeBasis(new THREE.Vector3(b.ux, 0, b.uz), new THREE.Vector3(0, 1, 0), new THREE.Vector3(-b.uz, 0, b.ux)).setPosition(b.x, 0, b.z);
    for (const [g, cc] of parts) walls.push(tint(g, cc).applyMatrix4(m));
    decks.push(deckGeometry(hl, hw).applyMatrix4(m));
    for (let k = paved.length - 2; k < paved.length; k++) paved[k].applyMatrix4(m);
  }
  const wallMesh = new THREE.Mesh(mergeGeometries(walls)!, stoneMaterial(PALETTE.stone.bridge, { block: 0.9 }));
  (wallMesh.material as THREE.MeshLambertMaterial).vertexColors = true;
  const ringMesh = new THREE.Mesh(mergeGeometries(paved)!, stoneMaterial(PALETTE.stone.voussoir, { block: 0.9, setts: true }));
  const deckMesh = new THREE.Mesh(mergeGeometries(decks)!, deckMaterial());
  for (const mesh of [wallMesh, ringMesh, deckMesh]) mesh.castShadow = mesh.receiveShadow = true;
  return [wallMesh, ringMesh, deckMesh];
}

/** The deck: cobbles along the parapets and onto the hump, the road's own sand (SAND_GLSL, at the same world spot, so
 *  it runs on without a seam) worn over them down the middle and over the ramps. */
function deckMaterial() {
  const mat = new THREE.MeshLambertMaterial();
  const u = { uCobble: c(PALETTE.stone.cobble) };
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, groundUniforms(), u);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec2 aDeck; varying vec3 vW; varying vec2 vDeck; varying vec2 vUv2;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vW = (modelMatrix * vec4(transformed, 1.0)).xyz; vDeck = aDeck; vUv2 = uv;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec3 uCobble; varying vec3 vW; varying vec2 vDeck; varying vec2 vUv2;' + NOISE_GLSL + SAND_GLSL)
      .replace('#include <color_fragment>', `#include <color_fragment>
  float lkH, lkSnowMul = 1.0;
#define LK_SNOW_MUL lkSnowMul
  {
    vec4 dS; float n2, sandH;
    vec3 sand = bdSand(vW.xz, sandH, dS, n2) * 0.96;
    // cobbles in rows across the road (uv: across, along, in meters), each its own shade, the joints sunk
    vec2 p = vUv2 / vec2(0.5, 0.36);
    float row = floor(p.y); p.x += lkHash(vec2(row, 3.0)) * 0.7;
    vec2 f = fract(p), id = floor(p);
    float aa = clamp(fwidth(p.y), 0.0, 0.5);
    float m = smoothstep(0.0, 0.12 + aa, f.x) * (1.0 - smoothstep(0.88 - aa, 1.0, f.x)) * smoothstep(0.0, 0.14 + aa, f.y) * (1.0 - smoothstep(0.86 - aa, 1.0, f.y));
    vec3 cob = uCobble * (0.8 + 0.3 * lkHash(id)) * mix(0.6, 1.0, m);
    // where the cobbles show: within a meter and a half of a parapet, away from the ramps' ends, edged by worn sand
    float show = (1.0 - smoothstep(0.9, 1.7, vDeck.x + (n2 - 0.5) * 0.9)) * smoothstep(1.2, 2.6, vDeck.y);
    diffuseColor.rgb = mix(sand, cob, show);
    lkH = mix(sandH, 0.02 * m, show);
    lkSnowMul = 1.0 - 0.7 * (1.0 - show); // the sand down the middle trodden, as the road's
  }`)
      .replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n  normal = lkBump(-vViewPosition, normal, lkH);');
    weathered(sh);
  };
  mat.customProgramCacheKey = () => 'deck';
  return mat;
}

/** The deck's top between the parapets, a grid riding the hump; uv is (across, along) in meters, aDeck how far a
 *  point is from the nearer parapet and from the nearer end. */
function deckGeometry(hl: number, hw: number) {
  const nu = 32, nv = 8, pos: number[] = [], uv: number[] = [], deck: number[] = [], idx: number[] = [];
  for (let a = 0; a <= nu; a++) for (let c2 = 0; c2 <= nv; c2++) {
    const u = -hl + (a / nu) * 2 * hl, v = -hw + (c2 / nv) * 2 * hw;
    pos.push(u, deckY(u, hl) + 0.02, v); uv.push(v, u); deck.push(hw - Math.abs(v), hl - Math.abs(u));
  }
  for (let a = 0; a < nu; a++) for (let c2 = 0; c2 < nv; c2++) { const k = a * (nv + 1) + c2; idx.push(k, k + 1, k + nv + 1, k + 1, k + nv + 2, k + nv + 1); }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('aDeck', new THREE.Float32BufferAttribute(deck, 2));
  g.setIndex(idx); g.computeVertexNormals();
  return g.toNonIndexed();
}

/** One face's ring of arch stones: a band `w` wide round the arch (an ellipse of half span a and rise b from y0),
 *  set `proud` out from the face at v = at (side: which way is out). uv is (along the ring, out from it) in meters,
 *  so the joints run radially. Its front, and the band's inner and outer edges back to the face. */
function archRing(a: number, b: number, y0: number, w: number, at: number, side: number) {
  const N = 20, proud = 0.1, pos: number[] = [], uv: number[] = [], idx: number[] = [];
  let s = 0;
  const pt = (t: number, r: number) => [Math.cos(t) * (a + r), y0 + Math.sin(t) * (b + r)];
  for (let k = 0; k <= N; k++) {
    const t = Math.PI - (k / N) * Math.PI;
    if (k) { const [x0, y1] = pt(t + Math.PI / N, w / 2), [x1, y2] = pt(t, w / 2); s += Math.hypot(x1 - x0, y2 - y1); }
    for (const [r, v] of [[0, at], [0, at + side * proud], [w, at + side * proud], [w, at]] as const) { const [x, y] = pt(t, r); pos.push(x, y, v); uv.push(s, r); }
  }
  for (let k = 0; k < N; k++) for (let e = 0; e < 3; e++) {
    const p = k * 4 + e, q = p + 4;
    if (side > 0) idx.push(p, q, p + 1, p + 1, q, q + 1); else idx.push(p, p + 1, q, p + 1, q + 1, q);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx); g.computeVertexNormals();
  return g.toNonIndexed();
}
